import { InfoCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { Checkbox, Collapse, Dropdown, Flex, Form, message, Modal, Slider, Spin, Switch, Tag, theme, Tooltip } from "antd";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { downloadBatchReportRequest, downloadFiberAnalysisPhaseTwoReportRequest, fetchSemAnalysisMaskDataRequest, modifyFiberRequest, phaseTwoFiberAnalysisClear, phaseTwoFiberAnalysisRequest, updatePhaseTwoFiberAnalysisPhaseRequest } from "src/store/actions/semAnalysis";
import { StoreState } from "src/store/configureStore";
import { StyledButton } from "src/styled_components/StyledButton";
import useTranslate from "src/utils/useTranslate";
import { IMAGE_ANALYSIS_TYPES } from "../../SEMAnalysisWrapper";
import { AsyncStates } from "src/constants";

type TProps = {
  minWidth: number;
  maxWidth: number;
  minLength: number;
  maxLength: number;
  showAllMasks: boolean;
  showOriginalImage: boolean;
  setWidthRangeThreshold: React.Dispatch<React.SetStateAction<number[]>>;
  setLengthRangeThreshold: React.Dispatch<React.SetStateAction<number[]>>;
  setShowAllMasks: React.Dispatch<React.SetStateAction<boolean>>;
  setShowOriginalImage: React.Dispatch<React.SetStateAction<boolean>>;
  showBBox: boolean;
  setShowBBox: React.Dispatch<React.SetStateAction<boolean>>;
  showSkeleton: boolean;
  setShowSkeleton: React.Dispatch<React.SetStateAction<boolean>>;
  hoveredMaskDetails: Record<string, any>;
  setShowGraphAnalysis: React.Dispatch<React.SetStateAction<boolean>>,
  showGraphAnalysis: boolean
  showJunctionPoints: boolean,
  setShowJunctionPoints: React.Dispatch<React.SetStateAction<boolean>>,
  showEndPointPoints: boolean,
  setShowEndPointDistance: React.Dispatch<React.SetStateAction<boolean>>,
  scaleHeightOverlay: number,
  setScaleHeightOverlay: React.Dispatch<React.SetStateAction<number>>
};

const DEFAULT_PARAMS = {
  COLOR_INTENSITY: [25, 255],
  DEFAULT_BILATERAL_THRESHOLD: 9,
};


const FiberAnalysisDetailsSidebar = ({
  minWidth,
  maxWidth,
  minLength,
  maxLength,
  showAllMasks,
  showOriginalImage,
  setWidthRangeThreshold,
  setLengthRangeThreshold,
  setShowAllMasks,
  setShowOriginalImage,
  showBBox,
  setShowBBox,
  showSkeleton,
  setShowSkeleton,
  hoveredMaskDetails,
  setShowGraphAnalysis,
  showGraphAnalysis,
  showJunctionPoints,
  setShowJunctionPoints,
  showEndPointPoints,
  setShowEndPointDistance,
  scaleHeightOverlay,
  setScaleHeightOverlay

}: TProps) => {
  const [t] = useTranslate();
  const params = useParams<any>();

  const [form] = Form.useForm();
  const { token } = theme.useToken();

  const [widthRange, setWidthRange] = useState<any[]>([]);
  const [lengthRange, setLengthRange] = useState<any[]>([]);
  const [colorIntensity, setColorIntensity] = useState(
    DEFAULT_PARAMS.COLOR_INTENSITY
  );
  const [bilateralThreshold, setBilateralThreshold] = useState<number>(
    DEFAULT_PARAMS.DEFAULT_BILATERAL_THRESHOLD
  );

  const { fiberAnalysisPhaseTwoReportDownloadStatus, semAnalysisMaskData, phaseTwoFiberAnalysisData, analysisTypes, downloadSegmentedImagesStatus, modifyFiberStatus, semAnalysisMaskDataStatus, fiberAnalysisPhaseTwoParameters, fiberAnalysisPhaseTwoParametersStatus } = useSelector(
    (state: StoreState) => state.semAnalysis
  );
  const dispatch = useDispatch();
  // const currentPhase = useMemo(() => {
  //   const phaseData = {
  //     ...(fiberAnalysisCurrentPhase ?? {})
  //   }
  //   if (!phaseData?.[params.fileId]) {
  //     phaseData[params.fileId] = "phase_1"
  //   }
  //   return phaseData
  // }, [fiberAnalysisCurrentPhase, params.fileId])


  const isPhaseTwoEnabled = useMemo(() => !!Object.keys(phaseTwoFiberAnalysisData?.[params?.fileId]?.data ?? {}).length, [params?.fileId, phaseTwoFiberAnalysisData])

  useEffect(() => {
    setWidthRange([minWidth, maxWidth]);
    setLengthRange([minLength, maxLength]);
    setWidthRangeThreshold([minWidth, maxWidth]);
    setLengthRangeThreshold([minLength, maxLength]);
  }, [
    maxLength,
    maxWidth,
    minLength,
    minWidth,
    setLengthRangeThreshold,
    setWidthRangeThreshold,
  ]);

  const resetParams = useCallback(() => {
    setBilateralThreshold(DEFAULT_PARAMS.DEFAULT_BILATERAL_THRESHOLD);
    setColorIntensity(DEFAULT_PARAMS.COLOR_INTENSITY);

    if (semAnalysisMaskData?.parameters) {
      const parameters = semAnalysisMaskData?.parameters;
      setColorIntensity(parameters?.color_threshold);
      setBilateralThreshold(parameters?.bilateral_threshold);
    }
  }, [semAnalysisMaskData?.parameters]);

  useEffect(() => {
    resetParams();
  }, [resetParams]);

  const modify = () => {
    dispatch(
      modifyFiberRequest({
        batch_id: params.batchId,
        file_id: params.fileId,
        thresholds: {
          bilateral_threshold: bilateralThreshold,
          color_threshold: colorIntensity,
        },
      })
    );
  };

  const resetAll = () => {
    setBilateralThreshold(9);
    resetParams();

    dispatch(
      modifyFiberRequest({
        batch_id: params.batchId,
        file_id: params.fileId,
        thresholds: {
          bilateral_threshold: DEFAULT_PARAMS.DEFAULT_BILATERAL_THRESHOLD,
          color_threshold: DEFAULT_PARAMS.COLOR_INTENSITY,
        },
      })
    );
  };

  const phaseTwoFiberAnalysisParameters = useMemo(() => {
    const params = fiberAnalysisPhaseTwoParameters

    const result = Object.values(params ?? {}).reduce((acc: {
      [key: string]: any
    }, curr: any) => {
      acc = { ...acc, ...(curr?.parameters ?? {}) }
      return acc
    }, {})

    return result
  }, [fiberAnalysisPhaseTwoParameters])


  const panelStyle: React.CSSProperties = {
    marginBottom: 24,
    background: "#434343",
    borderRadius: token.borderRadiusLG,
    border: 'none',
    color: "#fff"
  };

  const isAnyFileProcessing = useMemo(() => Object.values(phaseTwoFiberAnalysisData ?? {}).some((file) => file?.status === "processing"), [phaseTwoFiberAnalysisData])


  const onFinish = async (values: any) => {
    if (isAnyFileProcessing) return

    const parameterValues = Object.keys(phaseTwoFiberAnalysisParameters ?? {}).reduce((acc: any, key) => {
      acc[key] = values?.[key] ?? phaseTwoFiberAnalysisParameters?.[key].value
      return acc
    }, {})

    const joinScoreWeightSum = parseFloat((parameterValues?.distance_weight + parameterValues?.intensity_weight + parameterValues?.angle_weight + parameterValues?.curvature_weight).toFixed(10))

    if (joinScoreWeightSum !== 1) {
      return message.error(
        `Weights sum to ${joinScoreWeightSum}, not 1.0. Consider adjusting.`
      )
    }
    const junctionJoinScoreWeightSum = parseFloat((parameterValues?.junction_distance_weight + parameterValues?.junction_intensity_weight).toFixed(10))
    if (junctionJoinScoreWeightSum !== 1) {
      return message.error(
        ` Junction Weights sum to ${junctionJoinScoreWeightSum}, not 1.0. Consider adjusting.`
      )
    }

    dispatch(phaseTwoFiberAnalysisRequest({
      command: "start",
      batch_id: params.batchId,
      file_id: params.fileId,
      parameters: parameterValues
    }))
  };

  const cancelPhaseTwoRequest = async () => {
    dispatch(phaseTwoFiberAnalysisRequest({
      command: "cancel",
      batch_id: params.batchId,
      file_id: params.fileId,
    }))
  };

  const handlePhaseTwoReset = () => {
    if (isAnyFileProcessing) return
    // form.resetFields()
    const parameters = Object.keys(phaseTwoFiberAnalysisParameters ?? {}).reduce((acc: any, key) => {
      acc[key] = phaseTwoFiberAnalysisParameters?.[key].default
      return acc
    }, {})
    form.setFieldsValue(parameters)
    dispatch(phaseTwoFiberAnalysisRequest({
      command: "start",
      batch_id: params.batchId,
      file_id: params.fileId,
      parameters: parameters
    }))
  }

  const handleDownloadPhaseTwoDownloadReport = () => {
    dispatch(downloadFiberAnalysisPhaseTwoReportRequest({
      batch_id: params.batchId,
      file_id: params.fileId,
    }))
  }

  const handlePhaseOnedownloadReport = () => {
    const payload: any = {
      batch_id: params.batchId,
      file_ids: [params.fileId],
      analysis_type:
        analysisTypes[
        IMAGE_ANALYSIS_TYPES.FIBER_ANALYSIS as keyof typeof analysisTypes
        ],
    };

    dispatch(downloadBatchReportRequest(payload));
  }

  const formInitialValue = useMemo(() => Object.keys(phaseTwoFiberAnalysisParameters ?? {}).reduce((acc: any, key) => {
    acc[key] = phaseTwoFiberAnalysisParameters?.[key].value
    return acc
  }, {}), [phaseTwoFiberAnalysisParameters])

  useEffect(() => {
    form.setFieldsValue(formInitialValue)
  }, [formInitialValue, form])

  useEffect(() => {
    if (isPhaseTwoEnabled) {
      setShowAllMasks(false)
    }
  }, [isPhaseTwoEnabled, setShowAllMasks])

  const minColor = `rgb(${colorIntensity?.[0]}, ${colorIntensity?.[0]}, ${colorIntensity?.[0]})`
  const maxColor = `rgb(${colorIntensity?.[1]}, ${colorIntensity?.[1]}, ${colorIntensity?.[1]})`
  const rangeGradient = `linear-gradient(to right, ${minColor}, ${maxColor})`

  return (
    <div className="sidebar" style={{ width: "300px" }}>
      <div className="content" style={{ width: "300px" }}>
        <div className="option">
          <div className="label">{t("sem.showOriginalImage")}</div>
          <Switch
            checkedChildren={t("common.show")}
            checked={showOriginalImage}
            unCheckedChildren={t("common.hide")}
            onChange={(val: boolean) => {
              setShowOriginalImage(val);
            }}
            style={{
              outline: "none",
              width: "62px",
            }}
          />
        </div>

        {/* <div className="option">
          <div className="label">{t("sem.showAllMasks")}</div>
          <Switch
            checkedChildren={t("common.show")}
            checked={showAllMasks}
            disabled={showGraphAnalysis || isPhaseTwoEnabled}
            unCheckedChildren={t("common.hide")}
            onChange={(val: boolean) => {
              setShowAllMasks(val);
            }}
            style={{
              outline: "none",
              width: "62px",
            }}
          />
        </div> */}

        <div className="option">
          <div className="label">{t("sem.showBoundingBox")}</div>
          <Switch
            checkedChildren={t("common.show")}
            checked={showBBox}
            disabled={showGraphAnalysis}
            unCheckedChildren={t("common.hide")}
            onChange={(val: boolean) => {
              setShowBBox(val);
            }}
            style={{
              outline: "none",
              width: "62px",
            }}
          />
        </div>
        <div className="option">
          <div className="label">{t("sem.showSkeleton")}</div>
          <Switch
            disabled={showGraphAnalysis}
            checkedChildren={t("common.show")}
            checked={showSkeleton}
            unCheckedChildren={t("common.hide")}
            onChange={(val: boolean) => {
              setShowSkeleton(val);
            }}
            style={{
              outline: "none",
              width: "62px",
            }}
          />
        </div>

        {Object.entries(hoveredMaskDetails || {}).length > 0 && (
          <div className="multi-option">
            {Object.entries(hoveredMaskDetails).map(([key, value]) => (
              <Flex justify="space-between" key={key}>
                <div className="label">{key}</div>
                <Tag color="#424242">{value}</Tag>
              </Flex>
            ))}
          </div>
        )}

        <Spin
          style={{
            color: "#fff"
          }}
          spinning={phaseTwoFiberAnalysisData?.[params?.fileId]?.status === "processing"} tip={"Phase 2 is in progress"}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              borderRadius: "6px",
              padding: "16px",
              backgroundColor: "#262626",
              width: "100%",
            }}
          >
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
            }}>
              <div>{t("common.phaseOne")}</div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "1rem",
                }}
              >
                <Dropdown
                  placement="bottomRight"
                  menu={{
                    items: [
                      {
                        label: `${t("report.downloadReport")} (${t("common.phaseOne")})`,
                        key: "download",
                        onClick: () => handlePhaseOnedownloadReport(),
                        disabled: downloadSegmentedImagesStatus === AsyncStates.LOADING || modifyFiberStatus === AsyncStates.LOADING
                      },
                      {
                        label: t("common.editPhaseOne"),
                        key: "edit_phase_one",
                        disabled: !isPhaseTwoEnabled,
                        onClick: () => {
                          Modal.confirm({
                            title: `Are you sure you want to change the settings?`,
                            content: "Modifying will reset all the settings applied in stage 2",
                            icon: <InfoCircleOutlined />,
                            okText: t("common.yes"),
                            cancelText: t("common.no"),
                            okButtonProps: { style: { borderRadius: "12px" } },
                            cancelButtonProps: { style: { borderRadius: "12px" } },
                            onOk: () => {
                              dispatch(updatePhaseTwoFiberAnalysisPhaseRequest({
                                file_id: params.fileId,
                                phase: "phase_1"
                              }));
                              dispatch(phaseTwoFiberAnalysisClear({
                                file_id: params.fileId,
                                status: null,
                                message: null,
                                step: null,
                                progress: null,
                                data: null,
                              }))

                              dispatch(
                                fetchSemAnalysisMaskDataRequest({
                                  file_id: params.fileId,
                                  batch_id: params.batchId,
                                  analysis_type:
                                    analysisTypes[
                                    IMAGE_ANALYSIS_TYPES.FIBER_ANALYSIS as keyof typeof analysisTypes
                                    ],
                                })
                              );
                            },
                          })

                        }
                      },
                    ]
                  }}
                >
                  <StyledButton icon={<MoreOutlined />} size="middle" />
                </Dropdown>
              </div>
            </div>

            <div className="label">{t("sem.width")}</div>
            <Slider
              range
              disabled={isPhaseTwoEnabled}
              step={0.01}
              min={minWidth}
              max={maxWidth}
              value={widthRange}
              onChange={(val) => {
                setWidthRange(val)
              }}
              onAfterChange={(val) => {
                setWidthRangeThreshold(val)
              }}
              style={{ maxWidth: 300 }}
              rootClassName="sem-slider"
            />

            <div className="label">{t("sem.length")}</div>
            <Slider
              range
              disabled={isPhaseTwoEnabled}
              min={minLength}
              max={maxLength}
              value={lengthRange}
              onChange={(val) => setLengthRange(val)}
              onAfterChange={(val) => setLengthRangeThreshold(val)}
              style={{ maxWidth: 300 }}
              rootClassName="sem-slider"
            />


            <div className="label">{t("sem.scaleHeight")}</div>
            <Slider
              disabled={isPhaseTwoEnabled}
              step={0.1}
              min={6}
              max={20}
              defaultValue={scaleHeightOverlay}
              onChange={value => setScaleHeightOverlay(value)}
              style={{ maxWidth: 300 }}
              rootClassName="sem-slider"
            />

            <Collapse
              expandIconPosition="end"
              rootClassName="setting-collapse"
              items={[
                {
                  key: "1",
                  label: t("sem.advancedSettings"),
                  children: (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                      }}
                    >
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                      }}>
                        <div>
                          <div className="label">{t("sem.colorIntensity")}</div>
                          <Slider
                            disabled={isPhaseTwoEnabled}
                            range
                            step={1}
                            min={0}
                            max={255}
                            value={colorIntensity}
                            onChange={(val) => setColorIntensity(val)}
                            style={{ maxWidth: 300 }}
                            rootClassName="sem-slider" />
                        </div>
                        <div>
                          <div>
                            <div className="label">{`${t("sem.colorIntensity")} Preview`}</div>
                            <div className={"gradient__preview__intensity__container"} style={{ background: rangeGradient }}></div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="label">{t("sem.sensitivity")}</div>
                        <Slider
                          disabled={isPhaseTwoEnabled}
                          reverse
                          step={1}
                          min={4}
                          max={14}
                          value={bilateralThreshold}
                          onChange={(val) => setBilateralThreshold(val)}
                          style={{ maxWidth: 300, color: "#fff" }}
                          marks={{
                            4: "Strong",
                            9: "Medium",
                            14: "Weak",
                          }}
                          rootClassName="sem-slider sem-slider-no-track"
                        />
                      </div>

                      <div
                        style={{
                          display: "flex",
                          marginTop: "1rem",
                          gap: "1rem",
                        }}
                      >
                        <StyledButton
                          size="small" type="primary" onClick={modify} disabled={isPhaseTwoEnabled} className={isPhaseTwoEnabled ? "phase_2__btn__disabled" : ""}                        >
                          {t("common.apply")}
                        </StyledButton>
                        <StyledButton
                          size="small" onClick={resetAll} disabled={isPhaseTwoEnabled} className={isPhaseTwoEnabled ? "phase_2__btn__disabled" : ""}                      >
                          {t("sem.resetToDefault")}
                        </StyledButton>
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </Spin>

        <Spin
          style={{
            color: "#fff"
          }}
          tip={
            <div style={{
              display: "flex",
              gap: "0.5rem",
              flexDirection: "column",
              alignItems: "center"
            }}>
              {semAnalysisMaskDataStatus !== AsyncStates.LOADING && <>
                {phaseTwoFiberAnalysisData?.[params?.fileId]?.progress != null && (
                  <div>{t("common.progress")} : {phaseTwoFiberAnalysisData[params.fileId].progress}% </div>
                )}

                {phaseTwoFiberAnalysisData?.[params?.fileId]?.step != null && (
                  <div>{t("common.step")} : {phaseTwoFiberAnalysisData[params.fileId].step} </div>
                )}


                <StyledButton onClick={() => cancelPhaseTwoRequest()} danger>
                  {t("common.cancel")}
                </StyledButton>
              </>
              }
            </div>
          }
          spinning={phaseTwoFiberAnalysisData?.[params?.fileId]?.status === "processing" || semAnalysisMaskDataStatus === AsyncStates.LOADING || fiberAnalysisPhaseTwoParametersStatus === AsyncStates.LOADING}>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              borderRadius: "6px",
              padding: "16px",
              backgroundColor: "#262626",
              width: "100%",
            }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem"
            }}>
              <div>{t("common.phaseTwo")}</div>
              <div>
                <Tooltip title={`${t("report.downloadReport")} (${t("common.phaseTwo")})`}>
                  <StyledButton size="small" loading={fiberAnalysisPhaseTwoReportDownloadStatus === AsyncStates.LOADING} onClick={() => handleDownloadPhaseTwoDownloadReport()}
                    disabled={!phaseTwoFiberAnalysisData?.[params?.fileId]?.data} className={!phaseTwoFiberAnalysisData?.[params?.fileId]?.data ? "phase_2__btn__disabled" : ""}>
                    {t("report.downloadReport")}
                  </StyledButton>
                </Tooltip>
              </div>
            </div>

            <Form form={form} layout="vertical" onFinish={onFinish}
              disabled={isAnyFileProcessing}
            >
              <Collapse
                bordered={false}
                expandIconPosition="end"
                defaultActiveKey={['basic_parameters']}
                items={[...Object.keys(fiberAnalysisPhaseTwoParameters ?? {}).map((paramKey) => {
                  return {
                    key: paramKey,
                    style: panelStyle,
                    headerClass: "stage_2__fiber__collapse__header",
                    label: fiberAnalysisPhaseTwoParameters?.[paramKey]?.name,
                    children: <>{
                      Object.keys(fiberAnalysisPhaseTwoParameters?.[paramKey]?.parameters ?? {}).map((subParamKey) => {
                        const valueType = typeof fiberAnalysisPhaseTwoParameters?.[paramKey]?.parameters?.[subParamKey]?.default
                        const description = fiberAnalysisPhaseTwoParameters?.[paramKey]?.parameters?.[subParamKey]?.description
                        const range = fiberAnalysisPhaseTwoParameters?.[paramKey]?.parameters?.[subParamKey]?.range
                        const label = fiberAnalysisPhaseTwoParameters?.[paramKey]?.parameters?.[subParamKey]?.name

                        if (valueType === "number") {
                          return <Form.Item
                            key={subParamKey}
                            name={subParamKey}
                            label={<div style={{ color: "#fff" }}>{label}</div>}
                            tooltip={description}
                            rules={[{ required: true, message: `Please input ${label}` }]}
                          >
                            <Slider
                              step={range?.[1] > 1 ? 1 : 0.01}
                              min={range?.[0]}
                              max={range?.[1]}
                              rootClassName="sem-slider"
                            />
                          </Form.Item>
                        } else if (valueType === "boolean") {
                          return <Form.Item
                            key={subParamKey}
                            className="label"
                            name={subParamKey}
                            tooltip={description}
                            valuePropName="checked"
                            label={null}
                            rules={[{ required: true, message: `Please input ${label}` }]}
                          >
                            <Checkbox
                              defaultChecked={fiberAnalysisPhaseTwoParameters?.[paramKey]?.parameters?.[subParamKey]?.default ?? true}
                            >
                              <div
                                style={{
                                  color: "#fff"
                                }}>
                                {label}</div>
                            </Checkbox>
                          </Form.Item>
                        } else {
                          return <Form.Item>{label}</Form.Item>
                        }
                      })
                    }</>
                  }
                })]}
              />

              <div style={{
                display: "flex",
                marginTop: "1rem",
                gap: "1rem",
                color: "#fff"
              }}>
                <Form.Item>
                  <Tooltip
                    title={
                      isAnyFileProcessing
                        ? t("sem.fiberAnalysis.fileProcessingInfo")
                        : ""
                    }>
                    <StyledButton size="small" type="primary" htmlType="submit"
                      style={
                        {
                          ...(isAnyFileProcessing && { color: "#fff" })
                        }
                      }
                    >
                      {t("common.apply")}
                    </StyledButton>
                  </Tooltip>

                </Form.Item>

                <Form.Item >
                  <Tooltip
                    title={
                      isAnyFileProcessing
                        ? t("sem.fiberAnalysis.fileProcessingInfo")
                        : ""
                    }>
                    <StyledButton size="small" onClick={() => handlePhaseTwoReset()}
                      style={
                        {
                          ...(isAnyFileProcessing && { color: "#fff" })
                        }
                      }
                    >
                      {t("sem.resetToDefault")}
                    </StyledButton>
                  </Tooltip>
                </Form.Item>
              </div>
            </Form>
          </div>
        </Spin>

        <div className="option">
          <div className="label">{"Show Graph Representation"}</div>
          <Switch
            disabled={!phaseTwoFiberAnalysisData?.[params?.fileId]?.data}
            checkedChildren={t("common.show")}
            checked={showGraphAnalysis}
            unCheckedChildren={t("common.hide")}
            onChange={(val: boolean) => {
              setShowGraphAnalysis(val);
              if (val) {
                setShowBBox(false)
                setShowSkeleton(false)
                setShowAllMasks(false)
              }
            }}
            style={{
              outline: "none",
              width: "62px",
            }}
          />
        </div>


        {/* <div className="option">
          <div className="label">{"Show Junction Points"}</div>
          <Switch
            // disabled={!phaseTwoFiberAnalysisData?.[params?.fileId]?.data || !phaseTwoResult}
            checkedChildren={t("common.show")}
            checked={showJunctionPoints}
            unCheckedChildren={t("common.hide")}
            onChange={(val: boolean) => {
              setShowJunctionPoints(val);
            }}
            style={{
              outline: "none",
              width: "62px",
            }}
          />
        </div>

        <div className="option">
          <div className="label">{"Show EndPoint Distance"}</div>
          <Switch
            // disabled={!phaseTwoFiberAnalysisData?.[params?.fileId]?.data || !phaseTwoResult}
            checkedChildren={t("common.show")}
            checked={showEndPointPoints}
            unCheckedChildren={t("common.hide")}
            onChange={(val: boolean) => {
              setShowEndPointDistance(val);
            }}
            style={{
              outline: "none",
              width: "62px",
            }}
          />
        </div> */}

      </div>
    </div>
  );
};

export default memo(FiberAnalysisDetailsSidebar);
